// AICA Label Component - Meridian Dark Design System
// Accessible form label with professional styling

import * as React from 'react';
import { cn } from '@/lib/utils';

export interface LabelProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
  /**
   * Required field indicator
   */
  required?: boolean;
  /**
   * Size variant
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Style variant
   */
  variant?: 'default' | 'uppercase';
  /**
   * Error state
   */
  error?: boolean;
}

const Label = React.forwardRef<HTMLLabelElement, LabelProps>(
  (
    { className, required, size = 'md', variant = 'default', error, children, ...props },
    ref
  ) => {
    const sizes = {
      sm: 'text-xs',
      md: 'text-xs',
      lg: 'text-sm',
    };

    return (
      <label
        ref={ref}
        className={cn(
          // Base styles - Meridian Dark
          'block font-medium mb-1.5',

          // Default text color
          'text-fg-subtle',

          // Variant styles
          variant === 'uppercase' && [
            'uppercase tracking-wider font-semibold',
            'text-xs', // Override size for uppercase
          ],

          // Size variant (only if not uppercase)
          variant !== 'uppercase' && sizes[size],

          // Error state
          error && 'text-danger',

          // Hover state when label is clickable
          'hover:text-fg-muted',

          // Disabled state (when parent fieldset is disabled)
          'disabled:opacity-50 disabled:cursor-not-allowed',

          // Transitions
          'transition-all duration-150',

          className
        )}
        aria-invalid={error ? 'true' : undefined}
        {...props}
      >
        {children}
        {required && (
          <span
            className={cn('ml-1', error ? 'text-danger' : 'text-accent')}
            aria-label="required"
          >
            *
          </span>
        )}
      </label>
    );
  }
);
Label.displayName = 'Label';

export { Label };
